/**
 * Entrada do app (SPEC 10.1, 10.4, 10.5).
 * Ordem dos estilos: tokens → acréscimos da Rota → base → grade → impressão → vidro líquido.
 */
import './styles/tokens.css';
import './styles/tokens.rota.css';
import './styles/tokens.suite.css';
import './styles/base.css';
import './styles/grid.css';
import './styles/print.css';
import './styles/tokens.glass.css';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './app/App';
import { whenPainted } from './lib/afterPaint';
import { watchFontPreference } from './lib/font';

watchFontPreference();

const container = document.getElementById('root');
if (!container) throw new Error('Elemento #root não encontrado');
createRoot(container).render(
  <StrictMode>
    <App />
  </StrictMode>,
);

/** Service worker (10.5): precache do shell; nunca dados. Versão nova → toast "Nova versão disponível". */
async function registerServiceWorker(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  const { Workbox } = await import('workbox-window');
  const wb = new Workbox('/sw.js', { scope: '/' });
  wb.addEventListener('waiting', () => {
    // Na tela inicial, no login e nas telas de leitura do Painel não há nada a perder: a versão nova
    // entra sozinha (sem isso, quem só olha os números ficava preso na versão antiga guardada no
    // navegador). Ajustes e Estúdio têm formulário: lá a versão espera o "Atualizar".
    const path = window.location.pathname;
    const readOnly = path.startsWith('/painel') && !path.startsWith('/painel/ajustes');
    if (['/', '/entrar'].includes(path) || readOnly) {
      wb.addEventListener('controlling', () => window.location.reload());
      wb.messageSkipWaiting();
      return;
    }
    // Automações (28/10/2026): a área não tem o aviso "Nova versão", e quem atende ficava preso na
    // versão antiga. A nova entra sozinha, mas nunca no meio de uma resposta sendo digitada.
    if (path.startsWith('/automacoes')) {
      const typing = (): boolean => {
        const el = document.activeElement as HTMLInputElement | HTMLTextAreaElement | null;
        return !!el && (el.tagName === 'TEXTAREA' || el.tagName === 'INPUT') && !!el.value?.trim();
      };
      const apply = (): void => {
        if (typing()) {
          setTimeout(apply, 30_000);
          return;
        }
        wb.addEventListener('controlling', () => window.location.reload());
        wb.messageSkipWaiting();
      };
      apply();
      return;
    }
    // O aviso "Nova versão" é do shell do Painel: o store só desce aqui (fora do caminho de /entrar).
    void import('./data/store').then(({ rotaStore }) =>
      rotaStore.actions().setUpdateAvailable(true, () => {
        wb.addEventListener('controlling', () => window.location.reload());
        wb.messageSkipWaiting();
      }),
    );
  });
  await wb.register();
  // O painel fica aberto o dia todo sem recarregar: procura versão nova a cada 15 min.
  setInterval(() => void wb.update().catch(() => undefined), 15 * 60_000);
}

/** Web Vitals (10.1): na V1 só registram no console, em desenvolvimento. */
async function reportVitals(): Promise<void> {
  const { onCLS, onINP, onLCP } = await import('web-vitals');
  const log = (m: { name: string; value: number; rating: string }): void =>
    console.info(`[Rota · vitals] ${m.name} ${Math.round(m.value * 1000) / 1000} (${m.rating})`);
  onCLS(log);
  onINP(log);
  onLCP(log);
}

if (import.meta.env.PROD) {
  // Depois da primeira pintura útil (10.4): o workbox-window não disputa a rede com os números.
  const register = (): void => {
    void registerServiceWorker().catch((err: unknown) =>
      console.warn('[Rota] service worker não registrou', err),
    );
  };
  whenPainted(() => {
    if (typeof window.requestIdleCallback === 'function')
      window.requestIdleCallback(register, { timeout: 3000 });
    else setTimeout(register, 1000);
  });
} else {
  void reportVitals();
}
